import React, { useEffect, useState } from 'react';
import './Settings.css';
import './PromotionalPopupSettings.css';
import { getPromoPopupImageUrl, toAbsoluteUrl } from '../../utils/siteBranding';

const parseNonNegInt = (value, fallback, max) => {
  const n = parseInt(String(value), 10);
  if (Number.isNaN(n) || n < 0) return fallback;
  return Math.min(n, max);
};

/** Select all on focus so typing replaces 0 instead of appending (03, 08). */
function selectNumericInputOnFocus(e) {
  const el = e.target;
  if (el && typeof el.select === 'function') {
    requestAnimationFrame(() => el.select());
  }
}

/**
 * @param {object} props
 * @param {'site' | 'shopkeeper'} props.variant
 * @param {object} props.settings
 * @param {(patch: object) => void} props.onChange
 * @param {() => void | Promise<void>} props.onSave
 * @param {boolean} props.saving
 * @param {boolean} props.uploading
 * @param {(file: File) => void | Promise<void>} props.onImageFile
 * @param {() => void} props.onClearImage
 * @param {string} [props.imagePreviewUrl] — instant preview while uploading (data URL)
 * @param {boolean} [props.showPageTargets]
 */
const PromotionalPopupSettings = ({
  variant,
  settings,
  onChange,
  onSave,
  saving,
  uploading,
  onImageFile,
  onClearImage,
  imagePreviewUrl = '',
  showPageTargets = false,
}) => {
  const s = settings || {};
  const isShop = variant === 'shopkeeper';
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  const set = (patch) => onChange({ ...s, ...patch });

  const storedImageUrl = toAbsoluteUrl(getPromoPopupImageUrl(s));
  const displayImageUrl = (imagePreviewUrl || storedImageUrl || '').trim();
  const showImagePreview = !!displayImageUrl && !imageLoadFailed;

  useEffect(() => {
    setImageLoadFailed(false);
  }, [displayImageUrl]);

  const title = isShop ? 'Your shop promotional popup' : 'Site promotional popup';
  const intro = isShop
    ? 'Shows only on your public shop page (e.g. /your-handle) when visitors browse your products — not on the main site home.'
    : 'Main website popup for visitors. Choose which page types should show this promo. Shopkeepers manage their own popup separately in My Account.';

  return (
    <div className={`settings-card promo-settings-card promo-settings-card--${variant}`}>
      <div className="card-header">
        <h3>
          <i className="fas fa-window-maximize" aria-hidden /> {title}
        </h3>
        <button type="button" onClick={onSave} className="save-btn" disabled={saving || uploading}>
          <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`} aria-hidden /> Save
        </button>
      </div>
      <div className="card-content">
        <p className="form-hint logo-upload-hint promo-settings-intro">{intro}</p>

        {showPageTargets ? (
          <div className="form-group promo-page-targets">
            <label className="promo-page-targets__label">Show on pages</label>
            <div className="promo-page-targets__grid">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!s.promoPopupShowOnHome}
                  onChange={(e) => set({ promoPopupShowOnHome: e.target.checked })}
                />
                <span className="checkmark" />
                Main page (home)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!s.promoPopupShowOnProductPage}
                  onChange={(e) => set({ promoPopupShowOnProductPage: e.target.checked })}
                />
                <span className="checkmark" />
                Single product view
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!s.promoPopupShowOnShopkeeperPage}
                  onChange={(e) => set({ promoPopupShowOnShopkeeperPage: e.target.checked })}
                />
                <span className="checkmark" />
                Shopkeeper / creator page
              </label>
            </div>
            <small className="form-hint">
              When &quot;Shopkeeper page&quot; is off, only each shopkeeper&apos;s own popup runs on their page.
            </small>
          </div>
        ) : null}

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={!!s.promoPopupEnabled}
              onChange={(e) => set({ promoPopupEnabled: e.target.checked })}
            />
            <span className="checkmark" />
            Enable promotional popup
          </label>
        </div>

        <div className="form-group logo-upload-group">
          <label>Popup image</label>
          <div className="logo-upload-row promo-popup-upload-row">
            {showImagePreview ? (
              <div className="logo-preview-wrap promo-popup-preview-wrap">
                <img
                  src={displayImageUrl}
                  alt="Promo preview"
                  className="promo-popup-preview-img"
                  onLoad={() => setImageLoadFailed(false)}
                  onError={() => setImageLoadFailed(true)}
                />
                <button
                  type="button"
                  className="btn-remove-logo"
                  onClick={() => {
                    setImageLoadFailed(false);
                    onClearImage();
                  }}
                  disabled={saving || uploading}
                >
                  Remove
                </button>
              </div>
            ) : storedImageUrl || imagePreviewUrl ? (
              <div className="logo-preview-wrap promo-popup-preview-wrap">
                <div className="logo-preview-placeholder promo-popup-placeholder">
                  Image saved but preview failed to load. Save again or re-upload.
                </div>
                <button
                  type="button"
                  className="btn-remove-logo"
                  onClick={() => {
                    setImageLoadFailed(false);
                    onClearImage();
                  }}
                  disabled={saving || uploading}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="logo-preview-placeholder promo-popup-placeholder">No image</div>
            )}
            <label className={`logo-file-label ${uploading || saving ? 'disabled' : ''}`}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) onImageFile(file);
                }}
                disabled={saving || uploading}
                className="logo-file-input"
              />
              <i className="fas fa-cloud-upload-alt" aria-hidden />
              {uploading ? 'Uploading…' : 'Upload (max 5MB)'}
            </label>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor={`promo-delay-${variant}`}>Delay before show (seconds)</label>
            <input
              id={`promo-delay-${variant}`}
              type="number"
              min={0}
              max={600}
              className="form-input"
              value={s.promoPopupDelaySeconds ?? 3}
              onFocus={selectNumericInputOnFocus}
              onClick={selectNumericInputOnFocus}
              onChange={(e) =>
                set({ promoPopupDelaySeconds: parseNonNegInt(e.target.value, 0, 600) })
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor={`promo-autoclose-${variant}`}>Auto-close after (seconds)</label>
            <input
              id={`promo-autoclose-${variant}`}
              type="number"
              min={0}
              max={300}
              className="form-input"
              value={s.promoPopupAutoCloseSeconds ?? 8}
              onFocus={selectNumericInputOnFocus}
              onClick={selectNumericInputOnFocus}
              onChange={(e) =>
                set({ promoPopupAutoCloseSeconds: parseNonNegInt(e.target.value, 0, 300) })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionalPopupSettings;
